// AGENT-R: Data-Drill Drawer. Right-anchored slide-in panel that exposes
// the raw rows behind a visual or the source tables behind the report.
//
// Two modes (driven by useDrillStore):
//   - "visual"    → fetches /api/visual/{folder}/{visualId}/rows and renders
//                   the joined pre-aggregation frame in a paginated table.
//   - "rawTables" → tabbed view over the four source tables behind HSQ
//                   (Invoice / Item Analysis / Product Table / Client),
//                   each fetched from /api/tables/{name}/rows.
//
// The drawer also listens for the custom event ``hsq:open-raw-tables`` so the
// top-bar's "View Raw Tables" button (Agent L) can open it without holding a
// reference to the store.
//
// Footer offers a CSV download of the currently-displayed page.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api/client";
import { useDrillStore, type DrillMode } from "../state/drillStore";
import "./DataDrillDrawer.css";

// ── types ──────────────────────────────────────────────────────────────

interface ColumnMeta {
  name: string;
  dtype?: string;
}

type RowValue = string | number | boolean | null;

interface RowsResponse {
  columns: ColumnMeta[];
  rows: Record<string, RowValue>[];
  total: number;
  offset: number;
  limit: number;
  title?: string | null;
  joined_tables?: string[];
}

const PAGE_SIZE = 100;

// AGENT-R: the four source tables surfaced behind the HSQ report. Order
// matters — the first tab is the default selection on open.
const RAW_TABLE_TABS = ["Invoice", "Item Analysis", "Product Table", "Client"] as const;
type RawTableName = (typeof RAW_TABLE_TABS)[number];

// ── helpers ────────────────────────────────────────────────────────────

function csvEscape(v: RowValue): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowsToCsv(columns: ColumnMeta[], rows: Record<string, RowValue>[]): string {
  const header = columns.map((c) => csvEscape(c.name)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(r[c.name] ?? null)).join(","))
    .join("\n");
  return header + "\n" + body;
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function safePageFolder(pathname: string): string | null {
  // The app routes /p/:folder for every visible page. Pull the folder slug
  // out of the current URL so we can fire /api/visual/{folder}/{id}/rows.
  const m = pathname.match(/\/p\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function formatCell(v: RowValue): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    // Lightweight number presentation — avoid forcing locale-currency since
    // many columns are plain ints (refnums, counts).
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return String(v);
}

// ── inner table component ──────────────────────────────────────────────

// AGENT-R: small local table — duplicates the .tableex-* markup rather than
// importing TableExVisual directly (TableExVisual expects {spec, data, …}
// shaped props plus filter store integration that's inappropriate here).
function DrillTable({
  columns,
  rows,
}: {
  columns: ColumnMeta[];
  rows: Record<string, RowValue>[];
}) {
  if (!columns.length) {
    return <div className="drill-empty">No columns available.</div>;
  }
  return (
    <div className="drill-table-scroll">
      <table className="drill-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.name} title={c.dtype ? `${c.name} · ${c.dtype}` : c.name}>
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="drill-empty-cell">
                No rows
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => {
                  const v = r[c.name] ?? null;
                  const numeric = typeof v === "number";
                  return (
                    <td key={c.name} className={numeric ? "drill-td-num" : undefined}>
                      {formatCell(v)}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── shared body renderer for either mode ───────────────────────────────

function PagedRowsBody({
  endpoint,
  refreshKey,
}: {
  endpoint: string | null;
  refreshKey: string;
}) {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<RowsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset paging when the active key changes (new visual / new tab).
  useEffect(() => {
    setOffset(0);
    setData(null);
    setError(null);
  }, [refreshKey]);

  useEffect(() => {
    if (!endpoint) return;
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    const sep = endpoint.includes("?") ? "&" : "?";
    api
      .get<RowsResponse>(
        `${endpoint}${sep}limit=${PAGE_SIZE}&offset=${offset}`,
        ac.signal,
      )
      .then((resp) => {
        setData(resp);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setLoading(false);
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => ac.abort();
  }, [endpoint, offset, refreshKey]);

  const columns = data?.columns ?? [];
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd = Math.min(offset + PAGE_SIZE, total);

  const onDownloadCsv = useCallback(() => {
    if (!columns.length) return;
    const csv = rowsToCsv(columns, rows);
    const fname = `drill-${refreshKey.replace(/[^a-z0-9-]/gi, "_")}-p${offset}.csv`;
    downloadCsv(fname, csv);
  }, [columns, rows, refreshKey, offset]);

  return (
    <>
      <div className="drill-body">
        {loading && !data ? (
          <div className="drill-empty">Loading…</div>
        ) : error ? (
          <div className="drill-error">Failed to load: {error}</div>
        ) : (
          <DrillTable columns={columns} rows={rows} />
        )}
      </div>
      <div className="drill-footer">
        <div className="drill-footer-count">
          Showing {showingStart.toLocaleString()}–{showingEnd.toLocaleString()} of{" "}
          {total.toLocaleString()} rows
        </div>
        <div className="drill-footer-actions">
          <button
            type="button"
            className="drill-btn-secondary"
            onClick={onDownloadCsv}
            disabled={rows.length === 0}
            title="Download current page as CSV"
          >
            Download CSV
          </button>
          <button
            type="button"
            className="drill-btn-secondary"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0 || loading}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="drill-btn-secondary"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={showingEnd >= total || loading}
          >
            Next ›
          </button>
        </div>
      </div>
    </>
  );
}

// ── drawer ─────────────────────────────────────────────────────────────

export function DataDrillDrawer() {
  const mode = useDrillStore((s) => s.mode);
  const visualId = useDrillStore((s) => s.visualId);
  const close = useDrillStore((s) => s.close);
  const openRawTables = useDrillStore((s) => s.openRawTables);
  const { pathname } = useLocation();

  const [activeTab, setActiveTab] = useState<RawTableName>(RAW_TABLE_TABS[0]);

  // AGENT-R: bridge — Agent L's top-bar button dispatches `hsq:open-raw-tables`
  // on window. We turn that into a store mutation so the drawer can open
  // without the top-bar holding a direct reference.
  useEffect(() => {
    const handler = () => openRawTables();
    window.addEventListener("hsq:open-raw-tables", handler);
    return () => window.removeEventListener("hsq:open-raw-tables", handler);
  }, [openRawTables]);

  // Reset the active raw-tables tab whenever the drawer is reopened in
  // rawTables mode (UX: don't surprise the user with a sticky tab from a
  // previous session).
  useEffect(() => {
    if (mode === "rawTables") setActiveTab(RAW_TABLE_TABS[0]);
  }, [mode]);

  const folder = useMemo(() => safePageFolder(pathname), [pathname]);

  const endpoint = useMemo<string | null>(() => {
    if (mode === "visual") {
      if (!folder || !visualId) return null;
      return `/visual/${encodeURIComponent(folder)}/${encodeURIComponent(
        visualId,
      )}/rows`;
    }
    if (mode === "rawTables") {
      return `/tables/${encodeURIComponent(activeTab)}/rows`;
    }
    return null;
  }, [mode, folder, visualId, activeTab]);

  const refreshKey = useMemo(() => {
    if (mode === "visual") return `v:${folder ?? "_"}::${visualId ?? "_"}`;
    if (mode === "rawTables") return `t:${activeTab}`;
    return "_";
  }, [mode, folder, visualId, activeTab]);

  const title =
    mode === "visual"
      ? `Visual: ${visualId ?? "—"}`
      : mode === "rawTables"
      ? "Source Tables"
      : "";

  // Esc-to-close. Only bound while open.
  useEffect(() => {
    if (!mode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, close]);

  if (!mode) return null;

  return (
    <>
      <div
        className="drill-scrim"
        role="presentation"
        onClick={close}
      />
      <aside
        className={`drill-drawer drill-mode-${mode as Exclude<DrillMode, null>}`}
        aria-modal="true"
        role="dialog"
        aria-label={title}
      >
        <header className="drill-header">
          <div className="drill-title">{title}</div>
          <button
            type="button"
            className="drill-close"
            onClick={close}
            aria-label="Close drawer"
          >
            ×
          </button>
        </header>
        {mode === "rawTables" ? (
          <nav className="drill-tabs" role="tablist">
            {RAW_TABLE_TABS.map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={activeTab === t}
                className={`drill-tab${activeTab === t ? " drill-tab-active" : ""}`}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </button>
            ))}
          </nav>
        ) : null}
        <PagedRowsBody endpoint={endpoint} refreshKey={refreshKey} />
      </aside>
    </>
  );
}
